/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * First-login setup page (KRA-style): user clicks link from bulk welcome email,
 * lands here, sets a new password, then is logged in and redirected to registration/dashboard.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faEye,
  faEyeSlash,
  faSpinner,
  faExclamationCircle,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/FINAL LOGO AK.png';
import '../styles/ResetPassword.css';

const Setup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithUserData } = useAuth();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    const validateToken = async () => {
      if (!token || !token.trim()) {
        setError('Invalid or missing setup link.');
        setIsValidatingToken(false);
        return;
      }

      try {
        const url = buildApiUrl(`/api/auth/setup?token=${encodeURIComponent(token.trim())}`);
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json();

        if (response.ok && data.valid) {
          setTokenValid(true);
          setUserInfo({
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
          });
        } else {
          setError(data.error || 'This link has expired or is invalid. Please use the login page with your temporary password, or contact your organisation for a new link.');
        }
      } catch (err) {
        console.error('Setup token validation error:', err);
        setError('Failed to validate setup link. Please try again.');
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const validatePassword = (password) => {
    const minLength = 6;
    const errors = [];
    if (password.length < minLength) {
      errors.push(`At least ${minLength} characters`);
    }
    return { isValid: errors.length === 0, errors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const validation = validatePassword(formData.newPassword);
    if (!validation.isValid) {
      setError(`Password must contain: ${validation.errors.join(', ')}`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(buildApiUrl('/api/auth/setup/set-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token.trim(),
          newPassword: formData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Same shape as login – set session and redirect (registration or dashboard)
        const sessionData = {
          ...data,
          ...data.user,
          phone: data.user?.phone || data.phone || '',
        };
        loginWithUserData(sessionData, true);
        return;
      }

      setError(data.error || 'Failed to set password. Please try again.');
    } catch (err) {
      console.error('Set password error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-layout">
          <div className="reset-password-right" style={{ width: '100%' }}>
            <div className="reset-password-card" style={{ textAlign: 'center', padding: '3rem' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ marginBottom: '1rem' }} />
              <p>Checking your setup link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-layout">
          <div className="reset-password-right" style={{ width: '100%' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="reset-password-card"
            >
              <img src={logoImage} alt="AksharJobs" className="main-logo" style={{ width: 80, marginBottom: '1rem' }} />
              <div className="error-container">
                <div className="error-icon">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </div>
                <h2>Invalid or expired link</h2>
                <p>{error}</p>
                <Link to="/login" className="back-link">
                  Go to login
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-layout">
        <div className="reset-password-right" style={{ width: '100%' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="reset-password-card"
        >
          <img src={logoImage} alt="AksharJobs" className="main-logo" style={{ width: 80, marginBottom: '1rem' }} />
          <h1>Set your password</h1>
          <p className="setup-welcome" style={{ marginBottom: '1.5rem', color: '#64748b' }}>
            Hello{userInfo?.firstName ? `, ${userInfo.firstName}` : ''}! Choose a secure password to access your account.
          </p>

          <form onSubmit={handleSubmit} className="reset-password-form">
            {error && (
              <div className="error-message">
                <FontAwesomeIcon icon={faExclamationCircle} /> {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="newPassword">New password</label>
              <div className="password-input-container">
                <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm password</label>
              <div className="password-input-container">
                <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <button type="submit" className="reset-password-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Setting password...
                </>
              ) : (
                <>
                  Set password & continue <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </button>
          </form>

          <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: '#64748b' }}>
            You can also <Link to="/login">log in with your temporary password</Link> if you have it.
          </p>
        </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Setup;
